import * as React from 'react';
import { useToast } from '@cian/ui-kit';
import { TButtonTheme } from '@cian/ui-kit/button/types';
import { useDispatch, useSelector } from 'react-redux';

import { getComparisonStatus, getNewbuildingId } from '../../selectors/newbuilding';
import { getGALabel } from '../../selectors/newbuilding/getGALabel';
import { getIsAuthenticated } from '../../selectors/user';
import { getGeoSubdomain } from '../../selectors/geoSubdomain';
import { getUtmSource } from '../../selectors/utmSource';
import { requestAuthentication } from '../../actions/authentication';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../services/propertyComparison';
import { setComparisonStatus } from '../../slices/newbuildingReducer';
import { TDispatch } from '../../types/redux';
import { useApplicationContext } from '../../utils/applicationContext';
import { getGeoSubdomainUrl } from '../../utils/url';
import { CompareButtonControl } from './CompareButtonControl';
import { CompareButtonOnboarding } from './CompareButtonOnboarding';
import {
  CompareFlightButtonOnboarding,
  SliderModalLoadable,
  type TSliderModalProps,
  useCompareFlightOnboarding,
} from './CompareFlightOnboardingTooltip';
import { CompareToastContent } from './CompareToastContent';
import {
  COMPARE_ENABLED_CONFIG,
  COMPARE_FLIGHT_TRAP_ENABLED_CONFIG,
  COMPARE_FLIGHT_SLIDES_CONFIG,
  COMPARE_FLIGHT_UTM_SOURCE,
  EMPTY_FLIGHT_SLIDES_ARRAY,
} from './constants';
import { COMPARISON_ENTRYPOINTS_ENABLED_CONFIG } from '../Comparison/constants';
import {
  trackAddNewbuildingToComparison,
  trackClickAddComparisonPopup,
  trackClickRemoveComparisonPopup,
  trackRemoveNewbuildingFromComparison,
  type TComparisonEventSource,
} from './tracking';
import { useCompareOnboarding } from './useCompareOnboarding';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';
type TCompareFlightSlides = TSliderModalProps['slides'];

interface INewbuildingCompareButtonProps {
  hideText?: boolean;
  isHeader?: boolean;
  buttonTheme?: TButtonTheme;
  addToCompareText?: string;
  withOnboarding?: boolean;
  source: TComparisonEventSource;
}

export const NewbuildingCompareButton: React.FC<INewbuildingCompareButtonProps> = ({
  hideText = false,
  isHeader = false,
  buttonTheme,
  addToCompareText,
  withOnboarding = false,
  source,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch<TDispatch>();
  const comparisonStatus = useSelector(getComparisonStatus);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const newbuildingId = useSelector(getNewbuildingId);
  const gaLabel = useSelector(getGALabel);
  const geoSubdomain = useSelector(getGeoSubdomain);
  const utmSource = useSelector(getUtmSource);
  const [isCompareFlightModalOpen, setIsCompareFlightModalOpen] = React.useState(false);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;
  const isCompareFlightEnabled = config.get<boolean>(COMPARE_FLIGHT_TRAP_ENABLED_CONFIG) || false;
  const compareFlightSlides =
    config.get<TCompareFlightSlides>(COMPARE_FLIGHT_SLIDES_CONFIG) ?? EMPTY_FLIGHT_SLIDES_ARRAY;
  const isComparisonEntrypointsEnabled = config.get<boolean>(COMPARISON_ENTRYPOINTS_ENABLED_CONFIG) || false;
  const trackingSource = isComparisonEntrypointsEnabled ? source : undefined;
  const isAdded = comparisonStatus === 'added';
  const isComparisonAvailable = comparisonStatus === 'added' || comparisonStatus === 'available';
  const isOnboardingAvailable = withOnboarding && isCompareEnabled && isComparisonAvailable;
  const isCompareFlightOnboardingAvailable =
    withOnboarding && isCompareFlightEnabled && utmSource === COMPARE_FLIGHT_UTM_SOURCE;
  const { open: isOnboardingOpen, closeHandler } = useCompareOnboarding(
    isOnboardingAvailable && !isCompareFlightOnboardingAvailable,
  );
  const { isCompareFlightOnboardingOpen, closeHandler: compareFlightCloseHandler } = useCompareFlightOnboarding(
    isCompareFlightOnboardingAvailable,
  );

  const handleCompareFlightModalOpen = React.useCallback(() => {
    compareFlightCloseHandler();
    setIsCompareFlightModalOpen(true);
  }, [compareFlightCloseHandler]);

  const handleCompareFlightModalClose = React.useCallback(() => {
    setIsCompareFlightModalOpen(false);
  }, []);

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult, comparisonAction?: 'add' | 'remove') => {
      const onComparisonClick =
        comparisonAction === 'add' ? trackClickAddComparisonPopup : trackClickRemoveComparisonPopup;

      toast.open({
        type: result.success === false ? 'error' : 'success',
        hasCloseIcon: false,
        content: (
          <CompareToastContent
            text={result.text}
            comparisonUrl={result.comparisonUrl && getGeoSubdomainUrl(result.comparisonUrl, geoSubdomain)}
            onClick={() => onComparisonClick(newbuildingId, trackingSource)}
          />
        ),
      });
    },
    [toast, newbuildingId, trackingSource, geoSubdomain],
  );

  const handleClick = React.useCallback(async () => {
    const previousStatus = comparisonStatus;
    const nextStatus = isAdded ? 'available' : 'added';

    if (!isAuthenticated) {
      dispatch(requestAuthentication({}));

      return;
    }

    dispatch(setComparisonStatus(nextStatus));

    try {
      const result = !isAdded
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      if (result.success === false) {
        showComparisonToast(result);
        dispatch(setComparisonStatus(previousStatus));

        return;
      }

      showComparisonToast(result, isAdded ? 'remove' : 'add');

      if (!isAdded) {
        trackAddNewbuildingToComparison(newbuildingId, gaLabel, trackingSource);
      } else {
        trackRemoveNewbuildingFromComparison(newbuildingId, gaLabel, trackingSource);
      }
    } catch (_error) {
      dispatch(setComparisonStatus(previousStatus));
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [
    comparisonStatus,
    dispatch,
    gaLabel,
    httpApi,
    isAdded,
    isAuthenticated,
    logger,
    newbuildingId,
    showComparisonToast,
    trackingSource,
  ]);

  if (!isCompareEnabled || !isComparisonAvailable) {
    return null;
  }

  const button = (
    <CompareButtonControl
      hideText={hideText}
      isHeader={isHeader}
      buttonTheme={buttonTheme}
      addToCompareText={addToCompareText}
      isAdded={isAdded}
      onClick={handleClick}
    />
  );

  const buttonWithOnboarding = !withOnboarding ? (
    button
  ) : isCompareFlightOnboardingOpen ? (
    <CompareFlightButtonOnboarding
      open={isCompareFlightOnboardingOpen}
      onClose={compareFlightCloseHandler}
      onAboutComparisonClick={handleCompareFlightModalOpen}
    >
      {button}
    </CompareFlightButtonOnboarding>
  ) : isOnboardingOpen ? (
    <CompareButtonOnboarding open={isOnboardingOpen} onClose={closeHandler}>
      {button}
    </CompareButtonOnboarding>
  ) : (
    button
  );

  return (
    <>
      {buttonWithOnboarding}
      {isCompareFlightModalOpen && (
        <SliderModalLoadable slides={compareFlightSlides} onClose={handleCompareFlightModalClose} />
      )}
    </>
  );
};
